import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { faLock, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCardImageMutations } from '@hooks/card/image';
import { useCardAttributeLock } from '@hooks/card/use-card-attribute-lock';
import { analytics } from '@modules/analytics';
import { Text, Tooltip } from '@ui-lib';
import { type MouseEvent, useCallback, useContext, useMemo } from 'react';

import styles from '../../CardHeader.module.scss';
import { LockBlockButton } from '../../LockBlockButton';
import type { ImageType } from '../../types';
import { EmptyPicture } from '../EmptyPicture';
import { TemplatePlaceholder } from '../TemplatePlaceholder';
import { PicturePreviewButtonLabel } from './PicturePreviewButtonLabel';
import type { CardImage } from '.cache/__types__';

const LockIcon = () => <FontAwesomeIcon size="xs" icon={faLock} />;
const UnlockIcon = () => <FontAwesomeIcon size="xs" icon={faUnlock} />;
const EyeIcon = () => <FontAwesomeIcon size="xs" icon={faEye} />;
const EyeSlashIcon = () => <FontAwesomeIcon size="xs" icon={faEyeSlash} />;

interface PicturePreviewButtonProps {
  image?: Partial<CardImage> | null;
  onClick: () => void;
  /** When an attribute is blocked, it cannot be added to the card */
  isBlocked?: boolean;
  canLock?: boolean;
  isTemplate?: boolean;
  required: boolean;
  type: ImageType;
  disabled: boolean;
  blockedByTemplate?: boolean;
  /** If an image hasn't been added yet, you can BLOCK the attribute which prevents anyone else adding that image */
  toggleBlockAttribute?: () => void;
}

export const PicturePreviewButton = ({
  ref,
  canLock,
  isBlocked,
  onClick,
  required,
  image,
  type,
  disabled,
  toggleBlockAttribute,
  blockedByTemplate,
}: PicturePreviewButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const { flavour, isCardOwner } = useContext(cardEditContext);

  const { isLocked, canUnlock } = useCardAttributeLock({
    valueLocked: (isBlocked || image?.is_locked) ?? false,
    valueProvider: image?.value_provider ?? undefined,
    flavour,
  });

  const lockedByTemplate = Boolean(image?.template_id) && Boolean(image?.is_locked);

  const { update } = useCardImageMutations();

  const handleLockClicked = (e: MouseEvent) => {
    if (!image?.id) {
      // If there's no image, we block the attribute
      toggleBlockAttribute?.();
      return;
    }

    // If there is an image, we set is_locked on the CardImage
    toggleLock(image);
    e.preventDefault();
  };

  const url = image?.edited_url;

  const handleClick = useCallback(() => {
    if (isLocked) {
      return;
    }

    analytics.track({
      name: url ? 'editpicture_clicked' : 'addpicture_clicked',
      properties: { card_picture_type: type },
    });
    onClick();
  }, [isLocked, onClick, type, url]);

  /** Calls a mutation to lock/unlock the image */
  const toggleLock = useCallback(
    (image: Partial<CardImage>) => {
      if (!image.id) {
        return;
      }

      const newLockValue = !image.is_locked;
      const { __typename, ...restOfImage } = image;
      const { __typename: _, ...restOfMetadata } = image?.metadata ?? { __typename: null };
      update({
        ...restOfImage,
        metadata: {
          ...restOfMetadata,
        },
        id: image.id,
        is_locked: newLockValue,
      });
    },
    [update],
  );

  /** Render the correct UI depending on the state of the CardImage */
  const renderImage = useMemo(() => {
    switch (true) {
      // There's an image URL, so render the image
      case Boolean(url): {
        return (
          // relative position styling for placing the lock icon
          <div className={styles['image-container']}>
            <img src={url ?? ''} alt={`Preview of ${type}`} />
            <PicturePreviewButtonLabel type={type} isLocked={isLocked} canLock={canLock} />
          </div>
        );
      }
      case Boolean(image?.value_provider): {
        return (
          <div className={styles['image-container']}>
            <TemplatePlaceholder type={type} placeholderImageSize="normal" />
            <PicturePreviewButtonLabel type={type} isLocked={isLocked} canLock={canLock} />
          </div>
        );
      }
      default: {
        return <EmptyPicture type={type} required={required} />;
      }
    }
  }, [url, image, type, isLocked, canLock, required]);

  const displayLockIcon = () => {
    // if card owner - and not template
    if (isCardOwner && flavour !== CardFlavours.TEMPLATE) {
      // Show buttons if blocked by template - card owner cannot change
      if (blockedByTemplate || lockedByTemplate) {
        return true;
      }
      // Otherwise, don't show them as they are pointless
      return false;
    }

    const isPersonalCard = flavour === CardFlavours.PERSONAL;
    const imageToBeProvidedByMember =
      image && !image.edited_url && image.value_provider === 'member';
    if (imageToBeProvidedByMember) {
      return false;
    }
    if (isPersonalCard && !isLocked) {
      return false;
    }
    return true;
  };

  const getIcon = () => {
    if (!image?.edited_url && isBlocked) {
      return <EyeSlashIcon />;
    }

    if (!image?.edited_url && !isBlocked) {
      return <EyeIcon />;
    }

    if (image?.edited_url && image.is_locked) {
      return <LockIcon />;
    }

    if (image?.edited_url && !image.is_locked) {
      return <UnlockIcon />;
    }
  };

  const tooltipStrongLabelWhenUnlocked = url ? 'Lock: ' : 'Hide: ';
  const tooltipStrongLabelWhenLocked = url ? 'Unlock: ' : 'Show: ';
  const tooltipLabelContentWhenUnlocked = url
    ? 'Team members won’t be able to edit this field.'
    : 'Team members won’t be able to add this field.';
  const tooltipLabelContentWhenLocked = url
    ? 'Team members will be able to edit this field.'
    : 'Team members will be able to add this field.';

  const tooltipLabel = (
    <Text tone="neutralInverted" size="small">
      <strong>{isLocked ? tooltipStrongLabelWhenLocked : tooltipStrongLabelWhenUnlocked}</strong>
      {isLocked ? tooltipLabelContentWhenLocked : tooltipLabelContentWhenUnlocked}
    </Text>
  );

  const lockButton = (
    <Tooltip label={tooltipLabel}>
      <LockBlockButton
        locked={isLocked}
        canLock={canLock && canUnlock}
        position="top-right"
        onClick={handleLockClicked}
        icon={getIcon()}
      />
    </Tooltip>
  );

  return (
    <>
      <div className={styles['picture-preview-button-container']}>
        {displayLockIcon() && lockButton}
        <button
          ref={ref}
          className={styles['picture-preview']}
          onClick={handleClick}
          data-locked={isLocked}
          data-can-lock={canLock}
          data-empty={!url}
          disabled={disabled}
        >
          {renderImage}
        </button>
      </div>
    </>
  );
};

PicturePreviewButton.displayName = 'PicturePreviewButton';
